import { PusherEvent } from '@mondaygpt/constants'
import { useCallback, useEffect, useState } from 'react'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { IntegrationsService } from '../../../openapi/services/IntegrationsService'

interface UseConnectedPlatformsResult {
  loading: boolean
  hasAnyConnected: boolean
  connectedProviders: string[]
  refetch: () => Promise<void>
}

/**
 * Lightweight hook that tells us whether the current business has any
 * connected social platform. Powers the post-approve "connect your platform"
 * wow-moment prompt in the Weekly Cockpit.
 *
 * Fetches once on mount; caller is expected to invoke `refetch()` after a
 * successful OAuth so the UI reflects the new connection without a reload.
 */
export function useConnectedPlatforms(): UseConnectedPlatformsResult {
  const [loading, setLoading] = useState(true)
  const [connectedProviders, setConnectedProviders] = useState<string[]>([])

  const fetchOnce = useCallback(async () => {
    setLoading(true)
    try {
      const { integrations } = await IntegrationsService.integrationControllerGetIntegrationsV1()
      setConnectedProviders(
        integrations.filter(integration => integration.isConnected).map(i => i.provider)
      )
    } catch (error) {
      console.error('useConnectedPlatforms: failed to load integrations', error)
      setConnectedProviders([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchOnce()
  }, [fetchOnce])

  // Authoritative real-time signal. Callers' popup-close + status-check path
  // races the server's integration persistence (~800ms gap); this fires after
  // persistence completes, so every consumer auto-updates without a reload.
  useListenToPusherEvent<{ provider: string }>(PusherEvent.OAuthUpdated, () => {
    void fetchOnce()
  })

  return {
    loading,
    hasAnyConnected: connectedProviders.length > 0,
    connectedProviders,
    refetch: fetchOnce,
  }
}
